import { type WorkflowNodeId } from '@blaze-shared/api-contracts-domain';
import { getReachableWorkflowNodeIds, type Workflow } from '@blaze-shared/workflow-domain';

import { type WorkflowError, WorkflowGraphIntegrityError } from './errors';

/** The part of a workflow that graph integrity is decided by. */
export type WorkflowGraph = Pick<Workflow, 'id' | 'nodes' | 'startNodeId'>;

/**
 * Structural checks over the graph as a whole: every node reachable from a declared start
 * node, and no node id used twice. Node-level validation is meaningless while one of these
 * fails, so callers report these errors instead of the per-node ones.
 */
export function validateWorkflowGraphIntegrity(workflow: WorkflowGraph): WorkflowError<any>[] {
  const nodes = workflow.nodes ?? [];
  if (!nodes.length) {
    return [];
  }

  return [...findDuplicateNodeIdErrors(workflow), ...findOrphanNodeErrors(workflow)];
}

function findOrphanNodeErrors(workflow: WorkflowGraph): WorkflowGraphIntegrityError[] {
  const startNodeId = workflow.startNodeId;
  if (!startNodeId) {
    return [
      new WorkflowGraphIntegrityError(
        workflow.id,
        `Workflow ${workflow.id} does not have a start node defined`,
      ),
    ];
  }

  const reachableNodeIds = getReachableWorkflowNodeIds(workflow);

  return workflow.nodes
    .filter((node) => !reachableNodeIds.has(node.id))
    .map(
      (node) =>
        new WorkflowGraphIntegrityError(
          workflow.id,
          `Orphan node ${node.id} found in workflow ${workflow.id}`,
          node.id,
        ),
    );
}

function findDuplicateNodeIdErrors(workflow: WorkflowGraph): WorkflowGraphIntegrityError[] {
  const nodes = workflow.nodes ?? [];
  const idCount = new Map<WorkflowNodeId, number>();
  for (const node of nodes) {
    idCount.set(node.id, (idCount.get(node.id) ?? 0) + 1);
  }

  return [...idCount.entries()]
    .filter(([, count]) => count > 1)
    .map(
      ([nodeId]) =>
        new WorkflowGraphIntegrityError(
          workflow.id,
          `Duplicate node id ${nodeId} found in workflow ${workflow.id}`,
          nodeId,
        ),
    );
}
